/**
 * api/okr.ts —— 团队 OKR（§五 5.5）
 *
 * ⚠ 团队进度、O 进度、风险条数、全营平均、逾期天数**全部由服务端算好下发**，
 *   前端一个都不许自己求（红线 4）。看到这里出现 reduce / 求平均就是口径分叉的开始。
 * ⚠ `status='done'` 时 `progress` 由**服务端**补齐为 100；反向不成立（进度满不自动改状态）。
 *   前端不要抢着补——两处各补一次，哪天口径改了就只改得动一处。
 * 🔴 `saveProgress` 是**乐观锁**接口：`version` 必须是「打开页面时那一版」，
 *   不符会返回 `VERSION_CONFLICT` 并带 `conflicts` 逐条前后值。
 *   ⚠ 【松手才提交】——不要在滑块的 `input` 事件里调它，拖一格发一次请求会让冲突频繁误报。
 */
import { del, get, post, put, type ReqOptions } from './client.js';
import type {
  KeyResultView,
  KrStatus,
  ObjectiveView,
  OkrBoardView,
  OkrSortKey,
} from '@shared-types';

/** OKR 页整页数据（§五 5.5 补充 F1）。⚠ 左栏排序由服务端做，前端不再排一次 */
export const board = (teamId: string | null, sort: OkrSortKey, opts?: ReqOptions) => {
  const p = new URLSearchParams();
  if (teamId) p.set('teamId', teamId);
  if (sort) p.set('sort', sort);
  const s = p.toString();
  return get<OkrBoardView>(`/api/okr/board${s ? `?${s}` : ''}`, opts);
};

/** 某队的 O/KR 树（队伍详情页的「团队 OKR」区块）。⚠ 归档队也能查，只是不能改 */
export const objectivesOf = (teamId: string, opts?: ReqOptions) =>
  get<ObjectiveView[]>(`/api/teams/${encodeURIComponent(teamId)}/objectives`, opts);

export interface ObjectiveSaveInput {
  /** ⚠ 只有新建时带；编辑时传了不同的值服务端会拒（目标不能换队） */
  teamId?: string;
  title: string;
  desc: string | null;
  ownerId: string;
}

export const createObjective = (input: ObjectiveSaveInput) =>
  post<ObjectiveView>('/api/objectives', input);

export const updateObjective = (id: string, input: ObjectiveSaveInput) =>
  put<ObjectiveView>(`/api/objectives/${encodeURIComponent(id)}`, input);

/** 删除目标。⚠ 名下的 KR **一并删除**，二次确认的正文里必须写清条数 */
export const deleteObjective = (id: string) =>
  del<{ ok: true }>(`/api/objectives/${encodeURIComponent(id)}`);

export interface KrSaveInput {
  /** ⚠ 只有新建时带；编辑时传了不同的值服务端会拒（KR 不能换 O） */
  objectiveId?: string;
  title: string;
  /** 描述，可空，≤100 字；空传 null（服务端也会把空串存成 null） */
  desc: string | null;
  ownerId: string;
  /** 可空；⚠ 允许是过去的日期——那正是「已逾期 N 天」的来源 */
  due: string | null;
  progress: number;
  status: KrStatus;
}

export const createKr = (objectiveId: string, input: KrSaveInput) =>
  post<KeyResultView>('/api/key-results', { ...input, objectiveId });

export const updateKr = (id: string, input: KrSaveInput) =>
  put<KeyResultView>(`/api/key-results/${encodeURIComponent(id)}`, input);

export const deleteKr = (id: string) =>
  del<{ ok: true }>(`/api/key-results/${encodeURIComponent(id)}`);

/**
 * 进度 / 状态快调（滑块 + 数字框那一条路径）。
 * ⚠ 与双周报的 KR 同步**共用同一条服务端逻辑**，不是两套算法。
 */
export const saveProgress = (id: string, v: { progress: number; status: KrStatus; version: number }) =>
  put<KeyResultView>(`/api/key-results/${encodeURIComponent(id)}/progress`, { krId: id, ...v });
